import React from 'react';
import get from 'lodash/get';
import { colors } from 'ui/theme';
import { TipPlacement } from 'ui/tooltip';
import { NODE_TYPES } from '../flowchart';
import {
  StyledNodeTile,
  NodeTileText,
  NodeTileIconContainer,
  StyledTooltip,
} from './NodeTile.styled';

export type NodeTileNode = {
  /** The node display name, and what it will be filtered on */
  name?: string;
  /** The accent color - determines things such as the color of the icon */
  accentColor?: string;
  /** Node Data - will determine the structure of the new node after drag */
  data?: {
    /** Node Type - will determine the look/feel/functionality of the newly added node */
    type?: NODE_TYPES;
    [key: string]: any;
  };
  [key: string]: any;
};

export type NodeTileProps = {
  /** Default displayed node title */
  name?: string;
  /** The class to use for testing purposes */
  testClass?: string;
  /** The icon to render */
  Icon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  /** The icon category to which a tile belongs */
  IconCategory?: React.FunctionComponent;
  /** The color of the icon, focus border and other accents */
  accentColor?: string;
  /** Is the node disabled? */
  isDisabled?: boolean;
  /** Text appearing in the node tooltip */
  tooltipContent?: string;
  /** The node configuration */
  node?: NodeTileNode;
};

export const getNodeTileTestId = ({ testClass, node }: NodeTileProps) =>
  `node-tile-${testClass || get(node, 'data.type')}`;

export const NODE_TILE_ICON_TEST_ID = 'node-tile-icon';
export const NODE_TILE_TITLE_TEST_ID = 'node-tile-title';

const NodeTileCmp = ({
  name,
  testClass = '',
  accentColor = colors.grey90,
  Icon,
  IconCategory,
  isDisabled = false,
  tooltipContent,
  node,
  ...props
}: NodeTileProps) => {
  return (
    <StyledTooltip
      isEnabled={isDisabled && !!tooltipContent}
      placement={TipPlacement.Left}
      tooltipContent={tooltipContent}
    >
      <StyledNodeTile
        data-test={getNodeTileTestId({ testClass, node })}
        name={name}
        tabIndex={0}
        accentColor={accentColor}
        isDisabled={isDisabled}
        data-disabled={isDisabled}
        {...props}
      >
        {Icon && (
          <NodeTileIconContainer>
            <Icon data-test={NODE_TILE_ICON_TEST_ID} color={accentColor} />
            {IconCategory && <IconCategory />}
          </NodeTileIconContainer>
        )}
        <NodeTileText data-test={NODE_TILE_TITLE_TEST_ID}>{name}</NodeTileText>
      </StyledNodeTile>
    </StyledTooltip>
  );
};

export const NodeTile = React.memo(NodeTileCmp);

export default NodeTile;
